import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { ConversationAssistantUIModes } from './types';

/**
 * Interface for controlling the conversation assistant sidebar panel state.
 * Consumers (like Confluence) can provide their own implementation
 * to integrate with their sidebar management system. Currently the
 * same interface as AISmartButtonSidebarAPI in @atlassian/ai-smart-button.
 */
export type RovoExperienceSidebarAPI = {
	isSidebarOpen: () => boolean;
	openSidebar: () => void;
	closeSidebar: () => void;
};

export type RovoExperienceAPIState = {
	mode?: ConversationAssistantUIModes;
	isMiniModalAvailable: boolean;
	isSidebarAvailable: boolean;
	isFullScreenModalAvailable: boolean;
	initialPrompt?: string;
	wasAutoOpened: boolean;
};

const initialState: RovoExperienceAPIState = {
	mode: undefined,
	isMiniModalAvailable: false,
	isSidebarAvailable: true,
	isFullScreenModalAvailable: false,
	initialPrompt: undefined,
	wasAutoOpened: false,
};

const store = createStore({
	name: 'rovoExperienceAPIStore',
	initialState,
	actions: {
		// created for resetting store during tests
		reset:
			(): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState(initialState);
			},
		setMode:
			(
				mode: ConversationAssistantUIModes,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ mode, wasAutoOpened: false });
			},
		clearInitialPrompt:
			() =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ initialPrompt: undefined });
			},
		setWasAutoOpened:
			(wasAutoOpened: boolean) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ wasAutoOpened });
			},
		toggleConversationAssistantUIMode:
			({
				targetMode,
				sidebarApi,
				initialPrompt,
			}: {
				targetMode?: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
			}): (({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => {
				const { mode: currentMode } = getState();

				// If no target mode is provided, close any open modes
				if (!targetMode) {
					setState({ mode: undefined, wasAutoOpened: false });

					if (sidebarApi.isSidebarOpen()) {
						sidebarApi.closeSidebar();
					}

					return;
				}

				// Toggle mode functionality
				if (currentMode === targetMode) {
					switch (targetMode) {
						case ConversationAssistantUIModes.SIDEBAR: {
							/* Handles the edge case where the sidebar space is taken by another panel
							which results in currentMode = 'sidebar' && sidebarApi.isSidebarOpen() = false */
							if (!sidebarApi.isSidebarOpen()) {
								sidebarApi.openSidebar();
								return;
							}

							sidebarApi.closeSidebar();
							setState({ mode: undefined, wasAutoOpened: false });
							return;
						}
						case ConversationAssistantUIModes.MINI_MODAL: {
							setState({ mode: undefined, wasAutoOpened: false });
							return;
						}
						case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
							setState({ mode: undefined, wasAutoOpened: false });
							return;
						}
						default:
							return;
					}
				}

				// Switch mode functionality
				switch (targetMode) {
					case ConversationAssistantUIModes.SIDEBAR: {
						if (!sidebarApi.isSidebarOpen()) {
							sidebarApi.openSidebar();
						}
						setState({ mode: ConversationAssistantUIModes.SIDEBAR, wasAutoOpened: false });
						return;
					}

					case ConversationAssistantUIModes.MINI_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.MINI_MODAL,
							initialPrompt,
							wasAutoOpened: false,
						});
						return;
					}

					case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.FULL_SCREEN_MODAL,
							initialPrompt,
							wasAutoOpened: false,
						});
						return;
					}
				}
			},
		openConversationAssistantUIMode:
			({
				targetMode,
				sidebarApi,
				initialPrompt,
			}: {
				targetMode: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
			}): (({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => {
				const { mode: currentMode } = getState();

				// Already in the desired mode — ensure the sidebar is actually
				// visible (it may have been displaced by another panel) but
				// otherwise treat this as a no-op.
				if (currentMode === targetMode) {
					if (targetMode === ConversationAssistantUIModes.SIDEBAR && !sidebarApi.isSidebarOpen()) {
						sidebarApi.openSidebar();
					}
					return;
				}

				// Switch to the target mode
				switch (targetMode) {
					case ConversationAssistantUIModes.SIDEBAR: {
						if (!sidebarApi.isSidebarOpen()) {
							sidebarApi.openSidebar();
						}
						setState({ mode: ConversationAssistantUIModes.SIDEBAR, wasAutoOpened: false });
						return;
					}

					case ConversationAssistantUIModes.MINI_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						// wasAutoOpened is managed by the dedicated setWasAutoOpened action and reset on close/toggle/setMode.
						setState({
							mode: ConversationAssistantUIModes.MINI_MODAL,
							initialPrompt,
						});
						return;
					}

					case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.FULL_SCREEN_MODAL,
							initialPrompt,
						});
						return;
					}
				}
			},
		setIsMiniModalAvailable:
			(
				isMiniModalAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isMiniModalAvailable });
			},
		setIsSidebarAvailable:
			(
				isSidebarAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isSidebarAvailable });
			},
		setIsFullScreenModalAvailable:
			(
				isFullScreenModalAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isFullScreenModalAvailable });
			},
	},
});

const useStore = createHook(store);

export type RovoExperienceAPIStore = {
	mode?: ConversationAssistantUIModes;
	initialPrompt?: string;
	wasAutoOpened?: boolean;
	reset: () => void;
	clearInitialPrompt: () => void;
	setWasAutoOpened?: (wasAutoOpened: boolean) => void;
	toggleConversationAssistantUIMode: (args: {
		targetMode?: ConversationAssistantUIModes;
		sidebarApi: RovoExperienceSidebarAPI;
		initialPrompt?: string;
	}) => void;
	openConversationAssistantUIMode: (args: {
		targetMode: ConversationAssistantUIModes;
		sidebarApi: RovoExperienceSidebarAPI;
		initialPrompt?: string;
	}) => void;
	setIsMiniModalAvailable: (isMiniModalAvailable: boolean) => void;
	setIsSidebarAvailable: (isSidebarAvailable: boolean) => void;
	setIsFullScreenModalAvailable: (isFullScreenModalAvailable: boolean) => void;
	isMiniModalAvailable: boolean;
	isSidebarAvailable: boolean;
	isFullScreenModalAvailable: boolean;
};

export const useRovoExperienceAPIStore = (): RovoExperienceAPIStore => {
	const [
		{
			mode,
			isMiniModalAvailable,
			isSidebarAvailable,
			isFullScreenModalAvailable,
			initialPrompt,
			wasAutoOpened,
		},
		{
			reset,
			clearInitialPrompt,
			setWasAutoOpened,
			toggleConversationAssistantUIMode,
			openConversationAssistantUIMode,
			setIsMiniModalAvailable,
			setIsSidebarAvailable,
			setIsFullScreenModalAvailable,
		},
	] = useStore();

	return {
		mode,
		initialPrompt,
		wasAutoOpened,
		reset,
		clearInitialPrompt,
		setWasAutoOpened,
		toggleConversationAssistantUIMode,
		openConversationAssistantUIMode,
		setIsMiniModalAvailable,
		setIsSidebarAvailable,
		setIsFullScreenModalAvailable,
		isMiniModalAvailable,
		isSidebarAvailable,
		isFullScreenModalAvailable,
	};
};

export const useRovoExperienceAPIStoreActions: HookActionsFunction<
	BoundActions<
		RovoExperienceAPIState,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setMode: (
				mode: ConversationAssistantUIModes,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setWasAutoOpened: (
				wasAutoOpened: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			toggleConversationAssistantUIMode: ({
				targetMode,
				sidebarApi,
			}: {
				targetMode?: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
			}) => ({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			openConversationAssistantUIMode: ({
				targetMode,
				sidebarApi,
				initialPrompt,
			}: {
				targetMode: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
			}) => ({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsMiniModalAvailable: (
				isMiniModalAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsSidebarAvailable: (
				isSidebarAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsFullScreenModalAvailable: (
				isFullScreenModalAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
		}
	>
> = createActionsHook(store);
